import { useCallback, useEffect, useState } from 'react';
import { subscriptionsApi } from '@/api';
import { SUBSCRIPTION_VALIDATION_ENABLED } from '@/config/subscriptionPolicy';
import type { PlanUsageResponse } from '@/types/subscription';

export function usePlanLimits() {
  const [usage, setUsage] = useState<PlanUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setUsage(await subscriptionsApi.usage());
    } catch {
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!SUBSCRIPTION_VALIDATION_ENABLED) {
    return {
      usage,
      loading,
      reload,
      atStoreLimit: false,
      atEmployeeLimit: false,
      atProductLimit: false,
      storeLimitMessage: null,
      employeeLimitMessage: null,
      productLimitMessage: null,
    };
  }

  const atStoreLimit =
    usage != null && usage.maxStores != null && usage.storesUsed >= usage.maxStores;
  const atEmployeeLimit =
    usage != null && usage.maxEmployees != null && usage.employeesUsed >= usage.maxEmployees;
  const atProductLimit =
    usage != null && usage.maxProducts != null && usage.productsUsed >= usage.maxProducts;

  const storeLimitMessage = atStoreLimit
    ? `Your ${usage?.planName ?? 'plan'} allows up to ${usage?.maxStores} stores. Upgrade or request a custom plan.`
    : null;
  const employeeLimitMessage = atEmployeeLimit
    ? `Your plan allows up to ${usage?.maxEmployees} employees.`
    : null;
  const productLimitMessage = atProductLimit
    ? `Your plan allows up to ${usage?.maxProducts} products.`
    : null;

  return {
    usage,
    loading,
    reload,
    atStoreLimit,
    atEmployeeLimit,
    atProductLimit,
    storeLimitMessage,
    employeeLimitMessage,
    productLimitMessage,
  };
}
